import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEventHandler } from 'react';

export interface LongPressResult {
  onPointerDown: PointerEventHandler;
  onPointerUp: PointerEventHandler;
  onPointerLeave: PointerEventHandler;
  onPointerCancel: PointerEventHandler;
  holding: boolean;
}

// Generic pointer-event hook that fires `callback` after the pointer has been
// held on an element for `durationMs`. Returns handler props and a `holding`
// boolean so the caller can drive a visual countdown animation.
//
// Works on both touch and mouse via pointer events; cancel fires on lift or
// leave so the timer never fires unexpectedly.
export function useLongPress(callback: () => void, durationMs = 600): LongPressResult {
  const [holding, setHolding] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable ref so changing callback doesn't invalidate the memoised handlers.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Clean up any pending timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const cancel = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHolding(false);
  }, []);

  const onPointerDown = useCallback((): void => {
    setHolding(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setHolding(false);
      callbackRef.current();
    }, durationMs);
  }, [durationMs]);

  return {
    onPointerDown: onPointerDown as PointerEventHandler,
    onPointerUp: cancel as PointerEventHandler,
    onPointerLeave: cancel as PointerEventHandler,
    onPointerCancel: cancel as PointerEventHandler,
    holding,
  };
}
